// src/config/apiConfig.js
//
// Single source of truth for the backend origin AND for JWT auth. Every
// page/component imports from here instead of hardcoding
// "/api" or calling the bare fetch()/axios directly --
// so switching hosts, or changing how auth works, is a one-file change
// instead of a find-and-replace across every page.
//
// REACT_APP_API_ORIGIN can override the origin at build time (create a
// .env file at the project root with e.g.
// REACT_APP_API_ORIGIN=https://127.0.0.1:5000 -- Create React App only
// picks up REACT_APP_* variables, and only at build/dev-server start, so
// restart `npm start` after changing it).
//
// NOTE: the axios import has to live up here at the top of the file --
// CRA's ESLint config (import/first) treats an import statement lower in
// the file as an error, not just a warning, and fails the build.
import axios from "axios";

export const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "/api";

export const api = axios.create({baseURL: API_ORIGIN});

// ---------------------------------------------------------------
// JWT AUTH
// ---------------------------------------------------------------
// Every backend route now requires "Authorization: Bearer <token>"
// except a short public whitelist (login/OTP/password-reset, the
// pre-login Access Request form, and profile photo images -- see the
// matching comment block at the top of app.py). OtpVerify.jsx stores
// the token this module issues right after a successful login; every
// other page just calls authFetch()/the `api` axios instance below and
// gets the header attached automatically -- no page has to know the
// token even exists.
const TOKEN_KEY = "authToken";

export function getAuthToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Called whenever the backend responds 401 (missing/expired/invalid
// token, per @jwt_required in app.py) -- the stored session is no
// longer valid, so wipe it and send the user back to the login page
// instead of leaving them stuck on a page where every API call quietly
// fails. Skipped if we're already on the login page (avoids a redirect
// loop while the login/OTP screens themselves are calling their own
// public, unauthenticated endpoints).
function handleUnauthorized() {
  clearAuthToken();
  localStorage.removeItem("currentUser");
  if (typeof window !== "undefined" && window.location.pathname !== "/") {
    window.location.href = "/";
  }
}

// IMPL-01 (idle session timeout, backend half in app.py's
// jwt_required()): every authenticated response carries a freshened
// token in this header, with the same absolute expiry as the one that
// was sent but a bumped "last activity" mark. Picking it up here means
// an actively-used tab never gets logged out mid-work, while a tab
// left idle still expires server-side once IDLE_TIMEOUT_MINUTES
// passes with no requests at all. Needs CORS's expose_headers (app.py)
// to actually be readable cross-origin -- see the CORS(...) call there.
const REFRESHED_TOKEN_HEADER = "X-Refreshed-Token";

function pickUpRefreshedToken(refreshedToken) {
  if (refreshedToken) setAuthToken(refreshedToken);
}

// Drop-in replacement for the global fetch() -- same signature and
// return value (a Response), so every existing `fetch(url, opts)` call
// site becomes `authFetch(url, opts)` with no other changes needed.
// Automatically attaches the stored JWT (when logged in) and, on a 401
// back from the server, clears the stale session and redirects to
// login rather than letting the page silently fail forever.
export async function authFetch(url, options = {}) {
  const token = getAuthToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;

  // IMPL-03 correction: do NOT default credentials to "include" here.
  // A real bug this caused: a large number of routes in app.py (the
  // ones built on the older _cors_response() helper, not the global
  // CORS(app, ...) config -- e.g. /cost_estimate_api, used by Step 5's
  // Save) hardcode `Access-Control-Allow-Origin: *`. Per the CORS spec,
  // browsers unconditionally reject a credentialed request (any fetch
  // with credentials sent) whose response carries a wildcard origin --
  // that's not a bug in those routes, it's a hard browser rule. Forcing
  // "include" everywhere silently broke every one of those routes'
  // write calls with a network-level "Failed to fetch" (Step 5 Save
  // among others), even though the header-based auth those calls
  // already use was completely unaffected.
  //
  // The session cookie doesn't need to ride along on every existing
  // call anyway -- the Authorization header above already carries full
  // auth, exactly as it always has. Only the specific login/logout
  // calls that actually set or clear that cookie opt into credentials
  // explicitly (see OtpVerify.jsx, SignOut.jsx) -- respecting whatever
  // the caller passed here, defaulting to fetch's own normal behavior
  // otherwise.

const API_BASE = API_ORIGIN
  ? `${API_ORIGIN}/api`
  : "/api";

const requestPath = url.startsWith("/api/")
  ? url
  : `${API_BASE}/${url.replace(/^\/+/, "")}`;

const res = await fetch(requestPath, {
  ...options,
  headers
});

pickUpRefreshedToken(
  res.headers.get(REFRESHED_TOKEN_HEADER)
);

if (res.status === 401) {
  handleUnauthorized();
}

return res;
}

// Pre-configured axios instance -- import `api` (commonly aliased back
// to the name `axios` at the import site: `import { api as axios } from
// "../config/apiConfig"`) instead of the bare "axios" package anywhere
// that talks to the backend. Every axios.get/post/put/delete call then
// automatically carries the JWT, with zero changes needed at the actual
// call sites (baseURL is only used for relative URLs -- existing code
// that already builds full "${API_ORIGIN}/..." URLs keeps working
// exactly as before).
// IMPL-03 correction: NOT withCredentials:true by default -- same
// _cors_response()-wildcard-origin issue as authFetch above. Any call
// site that specifically needs the cookie can pass
// `{ withCredentials: true }` per-request (see OtpVerify.jsx).
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    pickUpRefreshedToken(response.headers?.[REFRESHED_TOKEN_HEADER.toLowerCase()]);
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);